import { ApolloProvider } from '@apollo/client';
import { CacheProvider, type EmotionCache } from '@emotion/react';
import CssBaseline from '@mui/material/CssBaseline';
import type { NextPageWithAuthOptions, Session } from '@side/next-auth';
import { SessionProvider } from '@side/next-auth/react';
import { ToastProvider } from '@side/pantry';
import type { AppProps } from 'next/app';
import dynamic from 'next/dynamic';
import Head from 'next/head';
import { type FC, type PropsWithChildren, Suspense } from 'react';
import { AppRefreshModal } from 'src/components/AppRefreshModal';
import { SetupMaze } from 'src/components/Initialization/SetupMaze';
import { ThemeProvider } from 'src/components/ThemeProvider';
import { UserInfoProvider } from 'src/components/UserInfoProvider';
import { initDatadog } from 'src/modules/datadog';
import { client } from 'src/modules/graphql';
import { initLogger } from 'src/modules/logger';
import { PantryProvider } from 'src/modules/pantry/PantryProvider';
import { GlobalStyles } from '@/theme/GlobalStyles';
import { DelayedToast } from '$delayed-toast';
import { ErrorBoundary, PageErrorFallback } from '$errors';
import { LaunchDarkly } from '../src/modules/launchdarkly/provider';
import { createEmotionCache } from '../src/theme/createEmotionCache';

const clientSideEmotionCache = createEmotionCache();

const Initialization = dynamic(
  () =>
    import('src/components/Initialization').then(
      (exported) => exported.Initialization,
    ),
  { ssr: false },
);

initDatadog();
initLogger();

type PageOptions = NextPageWithAuthOptions & {
  /**
   * The layout component to use for wrapping the page content.
   */
  Layout?: FC<PropsWithChildren>;
};

export interface SideAppProps
  extends AppProps<{ session: Session | null | undefined }> {
  emotionCache?: EmotionCache;
  Component: PageOptions;
}

export default function SideApp(props: SideAppProps) {
  const { Component, emotionCache = clientSideEmotionCache, pageProps } = props;
  const { session, ...additionalPageProps } = pageProps;
  const { Layout } = Component;

  const pageContent = <Component {...additionalPageProps} />;

  return (
    <>
      <SetupMaze />
      <ApolloProvider client={client}>
        <CacheProvider value={emotionCache}>
          <Head>
            <title>Side</title>
            <meta
              name="viewport"
              content="minimum-scale=1, initial-scale=1, width=device-width"
            />
          </Head>
          <SessionProvider
            session={session}
            basePath="/identity/api/auth"
            pageOptions={Component.auth}
          >
            <UserInfoProvider>
              <LaunchDarkly>
                <GlobalStyles />
                <ThemeProvider>
                  <PantryProvider>
                    <DelayedToast />
                    <ErrorBoundary fallback={PageErrorFallback}>
                      <ToastProvider />
                      <Suspense fallback={null}>
                        <Initialization />
                      </Suspense>
                      <CssBaseline />
                      {Layout ? <Layout>{pageContent}</Layout> : pageContent}
                      <AppRefreshModal />
                    </ErrorBoundary>
                  </PantryProvider>
                </ThemeProvider>
              </LaunchDarkly>
            </UserInfoProvider>
          </SessionProvider>
        </CacheProvider>
      </ApolloProvider>
    </>
  );
}
